import styled from 'styled-components'
import { rem } from 'polished'
import { createMapBounds, mapBoundsToMapboxBounds } from 'lib/maps/bounds'
import type { MapCameraPosition } from 'tripPlanner/api/types'
import type { MapCoordinate, MapMarkerComponent } from 'tripPlanner/utils/mapMarkerTransforms'
import {
  getMapPitchCamera,
  type MapPitchMode,
} from 'tripPlanner/components/ChatMap/map3DConfig'

// ---------------------------------------------------------------------------
// View state
// ---------------------------------------------------------------------------

export const DEFAULT_VIEW_STATE = { longitude: 100, latitude: 0, zoom: 2.0, pitch: 0, bearing: 0 }
export const DEFAULT_PADDING = { top: 80, bottom: 80, left: 80, right: 80 }
export const FALLBACK_MAX_ZOOM = 14
export const EMPTY_FIT_BOUNDS_COORDINATES: Array<MapCoordinate> = []

/** Padding so pins and side labels stay inside the in-chat card when fitting. */
export const CHAT_MAP_CARD_FIT_PADDING = {
  top: 32,
  bottom: 32,
  left: 32,
  // Labels sit to the right of pins; keep room without emptying the frame.
  right: 96,
} as const

// ---------------------------------------------------------------------------
// Map style
// ---------------------------------------------------------------------------

export const MAP_STYLE: React.CSSProperties = { width: '100%', height: '100%' }
export const FOG_CONFIG = {} as const

// ---------------------------------------------------------------------------
// Globe rotation
// ---------------------------------------------------------------------------

export const DEGREES_PER_SECOND = 1.5
export const ROTATION_DURATION_MS = 1000
export const LINEAR_EASING = (n: number): number => n

// ---------------------------------------------------------------------------
// Paint
// ---------------------------------------------------------------------------

export const PATH_LINE_PAINT = { 'line-color': '#047857', 'line-width': 3 }
export const REGION_FILL_PAINT = { 'fill-color': '#0ea5e9', 'fill-opacity': 0.15 }
export const REGION_OUTLINE_PAINT = { 'line-color': '#0284c7', 'line-width': 2 }

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MapViewState = { longitude: number; latitude: number; zoom: number }

export type MarkerSelectionData = {
  marker: MapMarkerComponent
}

export type { MapBounds } from 'lib/maps/bounds'

export type MapPathComponent = {
  label?: string
  points: Array<MapCoordinate>
}

export type MapRegionComponent = {
  label?: string
  polygon: Array<MapCoordinate>
}

export type Bounds = [[number, number], [number, number]]

// ---------------------------------------------------------------------------
// Pure functions
// ---------------------------------------------------------------------------

export function collectBoundsFromCoordinates(coords: Array<MapCoordinate>): Bounds | null {
  const mapBounds = createMapBounds(coords)
  if (!mapBounds) return null
  return mapBoundsToMapboxBounds(mapBounds)
}

export function flattenCoordinates({
  markers,
  paths,
  regions,
}: {
  markers: Array<MapCoordinate>
  paths: Array<MapPathComponent>
  regions: Array<MapRegionComponent>
}): Array<MapCoordinate> {
  const coords: Array<MapCoordinate> = []
  markers.forEach(marker => coords.push(marker))
  paths.forEach(path => {
    path.points.forEach(point => coords.push(point))
  })
  regions.forEach(region => {
    region.polygon.forEach(point => coords.push(point))
  })
  return coords
}

export function buildMapViewState(camera: MapCameraPosition, mapPitchMode: MapPitchMode) {
  return {
    longitude: camera.lng,
    latitude: camera.lat,
    zoom: camera.zoom,
    ...getMapPitchCamera(mapPitchMode),
  }
}

// ---------------------------------------------------------------------------
//  Instance counter
// ---------------------------------------------------------------------------

let mapWidgetInstance = 0
export const nextInstanceId = (): string => {
  mapWidgetInstance += 1
  return `chat-map-widget-${mapWidgetInstance}`
}

// ---------------------------------------------------------------------------
//  Styled components
// ---------------------------------------------------------------------------

export const Wrapper = styled.div`
  width: 100%;
  overflow: hidden;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  height: auto;
  display: block;
  flex-direction: initial;
  margin: ${rem(10)} 0;
  border-radius: ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &.fill-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    margin: 0;
    border-radius: 0;
    border: none;
  }
`

export const MapContainer = styled.div`
  position: relative;
  width: 100%;
  flex: initial;
  height: ${rem(340)};
  min-height: ${rem(300)};

  &.fill-container {
    flex: 1;
    height: 100%;
  }
`

export const MapLoadingSkeleton = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

export const NON_CLUSTER_MARKER_STYLE: React.CSSProperties = { zIndex: 1 }
